import React, { useState, useEffect } from 'react';
import { BookOpen, Award, Settings, Zap } from 'lucide-react';
import { GameState, CollectibleObject, Hero, Gradients, UserItemInstance } from '../types';
import AccountMenu from './AccountMenu';
import SparkleEffect from './SparkleEffect';
import ShinyIndicator from './ShinyIndicator';
import BurningEffect from './BurningEffect';
import ItemEffectsRenderer from './ItemEffectsRenderer';
import { useTranslation } from '../contexts/TranslationContext';
import { getLogoOverlayColor, getComplementaryDropShadow } from '../utils/colorUtils';
import { Capacitor } from '@capacitor/core';

interface HomeScreenProps {
  gameState: GameState;
  allObjects: CollectibleObject[];
  userInstances: UserItemInstance[];
  selectedHeroData: Hero | undefined;
  gradients: Gradients;
  onScreenChange: (screen: GameState['currentScreen']) => void;
  onObjectClick: (objectData: CollectibleObject) => void;
  onHeroClick: () => void;
  onElementSelect?: (element: any) => void;
  onOpenSettings?: () => void;
  onSwitchProfile?: () => void;
  activeChildName?: string | null;
}

const HomeScreen: React.FC<HomeScreenProps> = ({
  gameState,
  allObjects,
  userInstances,
  selectedHeroData,
  gradients,
  onScreenChange,
  onObjectClick,
  onHeroClick,
  onElementSelect,
  onOpenSettings,
  onSwitchProfile,
  activeChildName
}) => {
  const { homeObjects, heroConfig, backgroundConfig, editMode, selectedElement } = gameState;
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();

  // Fonction pour déterminer la couleur du titre selon le fond
  const getTitleColor = (gradient: string, intensity: string) => {
    const darkBackgrounds = ['space'];
    const mediumBackgrounds = ['forest', 'ocean'];
    const darkIntensities = ['dark'];
    
    if (darkBackgrounds.includes(gradient) || 
        (mediumBackgrounds.includes(gradient) && intensity === 'dark') ||
        darkIntensities.includes(intensity)) {
      return 'text-white shadow-black';
    }
    return 'text-gray-800 shadow-white';
  };

  // Messages du carrousel avec traductions
  const carouselMessages = [
    t("home.welcome_to_the_magical_adventure_of_lilhero", "Welcome to the magical adventure of LilHero! 🌟"),
    t("home.choose_your_hero_and_explore_fantastic_worlds", "Choose your hero and explore fantastic worlds! 🗺️"),
    t("home.collect_magical_objects_by_reading_stories", "Collect magical objects by reading stories! 📚"),
    t("home.decorate_your_world_with_your_discovered_treasures", "Decorate your world with your discovered treasures! ✨"),
  ];

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // Effet pour changer automatiquement le message
  useEffect(() => {
    const interval = 4000;
    const timer = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % carouselMessages.length);
    }, interval);

    return () => clearInterval(timer);
  }, [carouselMessages.length]);

  return (
    <div className={`relative h-screen-adaptive bg-gradient-to-b ${gradients[backgroundConfig.gradient][backgroundConfig.intensity]} overflow-hidden select-none`}>
      {/* Sélecteur de langue, profil enfant et menu compte */}
      <div className={`absolute z-50 flex items-center gap-3 ${isNative ? 'ios-header' : 'top-6 right-4'}`}>
        {/* Bouton changer de profil enfant */}
        {activeChildName && onSwitchProfile && (
          <button
            onClick={onSwitchProfile}
            className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg hover:bg-white transition-colors"
          >
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
              <span className="text-xs font-bold text-white">
                {activeChildName.charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="text-sm font-medium text-gray-700 max-w-[80px] truncate">
              {activeChildName}
            </span>
          </button>
        )}
        <AccountMenu onOpenSettings={onOpenSettings} />
      </div>

      {/* Logo avec overlay coloré (auto ou manuel) et blend mode */}
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-20 ios-logo">
        <div className="relative">
          {/* Logo original */}
          <img
            src="/logo.png"
            alt="Adventures Logo"
            className="h-20 w-auto transition-all duration-700 ease-in-out drop-shadow-lg"
          />
          {/* Couche colorée avec masque et blend mode */}
          <div
            className="absolute inset-0 transition-all duration-700 ease-in-out pointer-events-none"
            style={{
              backgroundColor: gameState.logoConfig.color
                ? `${gameState.logoConfig.color}B3` // Manual color with 70% opacity (B3 in hex)
                : getLogoOverlayColor(backgroundConfig.gradient, backgroundConfig.intensity), // Auto color
              WebkitMask: 'url(/logo.png) no-repeat center',
              WebkitMaskSize: 'contain',
              mask: 'url(/logo.png) no-repeat center',
              maskSize: 'contain',
              mixBlendMode: 'overlay'
            }}
          />
        </div>
      </div>

      {/* Carrousel textuel */}
      <div className="absolute top-32 left-1/2 transform -translate-x-1/2 z-20 ios-carousel">
        <div className={`text-center px-6 py-4 rounded-lg bg-white bg-opacity-90 backdrop-blur-sm shadow-lg max-w-md mx-auto transition-opacity duration-1000 ios-carousel-width`}>
          <p className="text-lg font-medium text-gray-800 leading-relaxed whitespace-pre-line">
            {carouselMessages[currentMessageIndex]}
          </p>
          {/* Indicateurs de pagination */}
          <div className="flex justify-center gap-2 mt-3">
            {carouselMessages.map((_, index: number) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  index === currentMessageIndex ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Objets flottants sur le fond */}
      {homeObjects.map((item, index) => {
        const objectData = allObjects.find(obj => obj.id === item.objectId);
        if (!objectData) return null;

        // Lookup the instance if instanceId is available
        const instance = item.instanceId
          ? userInstances.find(inst => inst.id === item.instanceId)
          : undefined;

        // Calculate unique animation delay based on item id for async floating
        const animationDelay = ((item.id * 0.7) % 3).toFixed(2);

        return (
          <div
            key={item.id}
            className={`absolute ${
              editMode
                ? `cursor-pointer ${selectedElement?.type === 'object' && selectedElement?.id === item.id
                  ? 'ring-4 ring-yellow-400 bg-yellow-100 bg-opacity-50 rounded-lg'
                  : 'hover:ring-2 hover:ring-blue-400'}`
                : 'pointer-events-none'
            }`}
            style={{
              left: `${item.x}%`,
              top: `${item.y}%`,
              width: '384px',
              height: '384px',
              transform: `translate(-50%, -50%) scale(${item.scale}) rotate(${item.rotation}deg)`,
              transformOrigin: 'center center'
            }}
            onClick={() => {
              // Cliquable uniquement en mode édition
              if (editMode && onElementSelect) {
                onElementSelect({ type: 'object', id: item.id, data: item });
              }
            }}
          >
            {/* Inner wrapper for float animation (separate from positioning transform) */}
            <div
              className={editMode ? '' : 'emoji-float-inner'}
              style={editMode ? {} : { animationDelay: `${animationDelay}s` }}
            >
              <img
                src={objectData.emoji}
                alt={objectData.name}
                className="w-full h-full object-contain"
              />

              {/* Render instance effects if available, otherwise fallback to legacy flags */}
              {instance?.appliedEffects && instance.appliedEffects.length > 0 ? (
                <ItemEffectsRenderer
                  effects={instance.appliedEffects}
                  size="large"
                  intensity="high"
                />
              ) : (
                <>
                  {/* Legacy effect rendering (backward compatibility) */}
                  <SparkleEffect
                    isShiny={objectData.isShiny}
                    size="large"
                    intensity="high"
                  />
                  <ShinyIndicator
                    isShiny={objectData.isShiny}
                    size="medium"
                    position="top-right"
                  />
                  <BurningEffect
                    isBurning={objectData.isBurning}
                    size="large"
                    intensity="high"
                  />
                </>
              )}
            </div>
          </div>
        );
      })}

      {/* Héros au centre */}
      <div
        className={`absolute ${
          editMode
            ? `cursor-pointer ${selectedElement?.type === 'hero'
              ? 'ring-4 ring-yellow-400 bg-yellow-100 bg-opacity-50 rounded-lg'
              : 'hover:ring-2 hover:ring-blue-400'}`
            : 'pointer-events-none'
        }`}
        style={{
          left: `${heroConfig.x}%`,
          top: `${heroConfig.y}%`,
          width: '384px',
          height: '384px',
          transform: `translate(-50%, -50%) scale(${heroConfig.scale}) rotate(${heroConfig.rotation}deg)`,
          transformOrigin: 'center center'
        }}
        onClick={() => {
          // Cliquable uniquement en mode édition
          if (editMode && onElementSelect) {
            onElementSelect({ type: 'hero', data: heroConfig });
          }
        }}
      >
        {/* Inner wrapper for bounce animation */}
        <div className={editMode ? '' : 'emoji-bounce-inner'}>
          {selectedHeroData?.emoji ? (
            <img
              src={selectedHeroData.emoji}
              alt={selectedHeroData.name || "Hero"}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-6xl">
              🦸
            </div>
          )}
        </div>
      </div>

      {/* Menu principal */}
      <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-90 backdrop-blur-sm safe-area-bottom">
        <div className="flex justify-around py-3 px-1">
          <button
            onClick={() => onScreenChange('books')}
            className="flex flex-col items-center gap-1 px-2 py-2 hover:bg-blue-100 rounded-lg transition-colors interactive-hover flex-1 min-w-0"
          >
            <BookOpen size={24} className="text-blue-600" />
            <span className="font-medium text-blue-600 text-xs truncate">{t("home.books", "Books")}</span>
          </button>

          <button
            onClick={() => onScreenChange('collection')}
            className="flex flex-col items-center gap-1 px-2 py-2 hover:bg-purple-100 rounded-lg transition-colors interactive-hover flex-1 min-w-0"
          >
            <Award size={24} className="text-purple-600" />
            <span className="font-medium text-purple-600 text-xs truncate">{t("home.collection", "Collection")}</span>
          </button>

          <button
            onClick={() => onScreenChange('world')}
            className="flex flex-col items-center gap-1 px-2 py-2 hover:bg-green-100 rounded-lg transition-colors interactive-hover flex-1 min-w-0"
          >
            <Settings size={24} className="text-green-600" />
            <span className="font-medium text-green-600 text-xs truncate">{t("home.my_world", "My World")}</span>
          </button>

          <button
            onClick={() => onScreenChange('transformation')}
            className="flex flex-col items-center gap-1 px-2 py-2 hover:bg-purple-100 rounded-lg transition-colors interactive-hover flex-1 min-w-0"
          >
            <Zap size={24} className="text-purple-600" />
            <span className="font-medium text-purple-600 text-xs truncate">{t("home.fusion", "Fusion")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomeScreen;